import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import clsx from "clsx";
import FolderIcon from "@material-ui/icons/FolderOutlined";
import FolderOpenIcon from "@material-ui/icons/FolderOpenOutlined";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddIcon from "@material-ui/icons/Add";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import PlayArrowIcon from "@material-ui/icons/PlayArrowOutlined";
import StopIcon from "@material-ui/icons/StopOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DriveFileMoveIcon from "@material-ui/icons/SubdirectoryArrowRightOutlined";
import OpenIcon from "@material-ui/icons/OpenInNewOutlined";
import FileIcon from "@material-ui/icons/InsertDriveFileOutlined";
import LinkOffIcon from "@material-ui/icons/LinkOff";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useConfirm } from "../../components/ConfirmDialog";
import { useAudioPreview } from "../../utils/audioPreview";

const useStyles = makeStyles(theme => ({
  page: { display: "flex", height: "100%", minHeight: 0, background: theme.app.chatBg },
  // ---- folder tree rail ----
  side: { width: 256, flexShrink: 0, borderRight: `1px solid ${theme.app.border}`, background: "#fff", display: "flex", flexDirection: "column" },
  sideHead: { display: "flex", alignItems: "center", padding: theme.spacing(2, 2, 1) },
  sideTitle: { fontWeight: 600, fontSize: 13, color: theme.app.ink },
  tree: { flex: 1, overflowY: "auto", padding: theme.spacing(0.5, 1) },
  node: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 8px",
    margin: "1px 0",
    borderRadius: 7,
    cursor: "pointer",
    color: theme.app.textSecondary,
    position: "relative",
    "&:hover": { background: theme.app.lineSoft },
    "&:hover $nodeMenu": { visibility: "visible" },
  },
  nodeActive: {
    background: "#F0F2F5",
    color: theme.app.ink,
    "&::before": { content: '""', position: "absolute", left: -4, top: 6, bottom: 6, width: 2.5, borderRadius: 2, background: theme.app.green },
  },
  caret: { width: 16, display: "flex", alignItems: "center", justifyContent: "center", color: theme.app.textTertiary, flexShrink: 0 },
  nodeIcon: { display: "flex", fontSize: 17, color: theme.app.textTertiary, flexShrink: 0 },
  nodeLabel: { flex: 1, fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  nodeCount: { fontFamily: theme.app.mono, fontSize: 11, color: theme.app.textTertiary },
  nodeMenu: { visibility: "hidden", padding: 1, color: "inherit" },
  sideFoot: { padding: theme.spacing(1.25) },
  ghostBtn: { width: "100%", justifyContent: "center", border: `1px solid ${theme.app.border}`, color: theme.app.textSecondary, fontSize: 12.5, fontWeight: 500, textTransform: "none", borderRadius: 8, boxShadow: "none", "&:hover": { background: theme.app.lineSoft, boxShadow: "none" } },
  // ---- main ----
  main: { flex: 1, minWidth: 0, overflowY: "auto" },
  wrap: { width: "100%", maxWidth: 1100, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
  header: { display: "flex", alignItems: "center", gap: 10, marginBottom: theme.spacing(2) },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: theme.app.ink },
  titleCount: { fontFamily: theme.app.mono, fontSize: 12, color: theme.app.textTertiary },
  grow: { flexGrow: 1 },
  usage: { display: "flex", gap: theme.spacing(3), padding: theme.spacing(1.5, 2), border: `1px solid ${theme.app.border}`, borderRadius: 10, background: "#fff", marginBottom: theme.spacing(2.5) },
  metric: { display: "flex", flexDirection: "column" },
  metricVal: { fontFamily: theme.app.mono, fontSize: 20, fontWeight: 600, color: theme.app.ink },
  metricLbl: { fontSize: 11, color: theme.app.textTertiary, textTransform: "uppercase", letterSpacing: "0.05em" },
  tbl: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 10, overflow: "hidden" },
  fhead: { display: "grid", gridTemplateColumns: "40px 1fr 110px 130px 96px", alignItems: "center", height: 38, padding: "0 12px", borderBottom: `1px solid ${theme.app.border}`, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", color: theme.app.textTertiary, fontWeight: 600 },
  frow: { display: "grid", gridTemplateColumns: "40px 1fr 110px 130px 96px", alignItems: "center", height: 46, padding: "0 12px", borderBottom: `1px solid ${theme.app.lineSoft}`, "&:hover": { background: theme.app.hover }, "&:last-child": { borderBottom: "none" } },
  fname: { fontSize: 13, color: theme.app.ink, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  fmeta: { fontFamily: theme.app.mono, fontSize: 11.5, color: theme.app.textSecondary },
  facts: { display: "flex", justifyContent: "flex-end", gap: 2, color: theme.app.textTertiary },
  empty: { padding: theme.spacing(6, 2), textAlign: "center", color: theme.app.textTertiary, fontSize: 13 },
}));

const fmtBytes = b => {
  if (!b) return "0 KB";
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDate = d => {
  try {
    return new Date(d).toLocaleDateString("pt-BR");
  } catch (e) {
    return "—";
  }
};

const Storage = () => {
  const classes = useStyles();
  const { confirm, prompt } = useConfirm();
  const { playingUrl, toggle, stop } = useAudioPreview();
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [rootId, setRootId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [folderMenu, setFolderMenu] = useState(null); // { anchor, folder }
  const [moveMenu, setMoveMenu] = useState(null); // { anchor, file }

  // ?file=<id> (link que o agente registra no resultado): abre a pasta do arquivo
  // e destaca a linha
  const linkedFileRef = useRef(
    (() => {
      try {
        return Number(new URLSearchParams(window.location.search).get("file")) || null;
      } catch (e) {
        return null;
      }
    })()
  );
  const [highlight, setHighlight] = useState(linkedFileRef.current);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/storage");
      setFolders(data.folders || []);
      setFiles(data.files || []);
      setTotal(data.totalBytes || 0);
      setRootId(data.rootId || null);
      const linked = linkedFileRef.current;
      linkedFileRef.current = null; // só no 1º carregamento
      const target = linked ? (data.files || []).find(f => f.id === linked) : null;
      if (target) {
        // expande os ancestrais da pasta do arquivo
        const byId = {};
        (data.folders || []).forEach(f => { byId[f.id] = f; });
        const open = {};
        for (let f = byId[target.folderId]; f; f = byId[f.parentId]) open[f.id] = true;
        setSelected(target.folderId);
        setExpanded(e => ({ ...e, ...open, [data.rootId]: true }));
        return;
      }
      setSelected(s => s || data.uploadsId || data.rootId || null);
      setExpanded(e => ({ ...e, [data.rootId]: true }));
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    load();
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const childrenOf = pid => folders.filter(f => f.parentId === pid);
  const fileCountOf = fid => files.filter(f => f.folderId === fid).length;
  const selectedFolder = folders.find(f => f.id === selected) || null;
  const visibleFiles = files.filter(f => f.folderId === selected);

  const createFolder = async parentId => {
    setFolderMenu(null);
    const name = await prompt({ title: "Nova pasta", label: "Nome da pasta", confirmText: "Criar" });
    if (!name || !name.trim()) return;
    try {
      await api.post("/storage/folders", { name: name.trim(), parentId });
      load();
    } catch (err) {
      toastError(err);
    }
  };
  const renameFolder = async f => {
    setFolderMenu(null);
    const name = await prompt({ title: "Renomear pasta", label: "Nome", defaultValue: f.name, confirmText: "Renomear" });
    if (!name || !name.trim() || name.trim() === f.name) return;
    try {
      await api.put(`/storage/folders/${f.id}`, { name: name.trim() });
      load();
    } catch (err) {
      toastError(err);
    }
  };
  const deleteFolder = async f => {
    setFolderMenu(null);
    if (!(await confirm({ title: "Excluir pasta", message: `Excluir a pasta "${f.name}"?`, confirmText: "Excluir", danger: true }))) return;
    try {
      await api.delete(`/storage/folders/${f.id}`);
      if (selected === f.id) setSelected(f.parentId || rootId);
      load();
    } catch (err) {
      toastError(err);
    }
  };
  const moveFile = async (file, folderId) => {
    setMoveMenu(null);
    try {
      await api.put(`/storage/files/${file.id}`, { folderId });
      load();
    } catch (err) {
      toastError(err);
    }
  };
  const isAudio = file => String(file.contentType || "").startsWith("audio/");
  // arquivo privado (recebido pelo agente) só baixa autenticado: busca o conteúdo
  // e abre numa aba. A aba abre ANTES do await (senão o navegador bloqueia).
  const openFile = async file => {
    setHighlight(file.id);
    if (!file.isPrivate) {
      window.open(file.url, "_blank", "noopener");
      return;
    }
    const tab = window.open("", "_blank");
    try {
      const { data } = await api.get(`/storage/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      if (tab) tab.close();
      toastError(err);
    }
  };

  // revoga TODOS os links públicos já gerados deste arquivo (nó "Tornar link público")
  const revokePublic = async file => {
    if (!(await confirm({ title: "Revogar link público", message: `Os links públicos de "${file.name}" vão parar de funcionar. O fluxo gera um novo na próxima vez que rodar.`, confirmText: "Revogar", danger: true }))) return;
    try {
      await api.post(`/storage/files/${file.id}/revoke-public`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const removeFile = async file => {
    if (!(await confirm({ title: "Excluir arquivo", message: `Excluir "${file.name}"?`, confirmText: "Excluir", danger: true }))) return;
    try {
      await api.delete(`/audio/uploads/${file.id}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  // recursive tree node
  const renderNode = (folder, depth) => {
    const kids = childrenOf(folder.id);
    const isOpen = !!expanded[folder.id];
    const hasKids = kids.length > 0;
    return (
      <React.Fragment key={folder.id}>
        <div
          className={clsx(classes.node, selected === folder.id && classes.nodeActive)}
          style={{ paddingLeft: 8 + depth * 14 }}
          onClick={() => setSelected(folder.id)}
        >
          <span
            className={classes.caret}
            onClick={e => { e.stopPropagation(); setExpanded(x => ({ ...x, [folder.id]: !isOpen })); }}
          >
            {hasKids ? (isOpen ? <ExpandMoreIcon fontSize="inherit" /> : <ChevronRightIcon fontSize="inherit" />) : null}
          </span>
          <span className={classes.nodeIcon}>
            {isOpen && hasKids ? <FolderOpenIcon fontSize="inherit" /> : <FolderIcon fontSize="inherit" />}
          </span>
          <span className={classes.nodeLabel}>{folder.name}</span>
          <span className={classes.nodeCount}>{fileCountOf(folder.id) || ""}</span>
          {!folder.system && (
            <IconButton size="small" className={classes.nodeMenu} onClick={e => { e.stopPropagation(); setFolderMenu({ anchor: e.currentTarget, folder }); }}>
              <MoreVertIcon style={{ fontSize: 16 }} />
            </IconButton>
          )}
        </div>
        {isOpen && kids.map(k => renderNode(k, depth + 1))}
      </React.Fragment>
    );
  };

  const root = folders.find(f => f.id === rootId);

  return (
    <div className={classes.page}>
      <div className={classes.side}>
        <div className={classes.sideHead}>
          <Typography className={classes.sideTitle}>Armazenamento</Typography>
        </div>
        <div className={classes.tree}>{root ? renderNode(root, 0) : null}</div>
        <div className={classes.sideFoot}>
          <Button className={classes.ghostBtn} startIcon={<AddIcon />} onClick={() => createFolder(selected || rootId)}>
            Nova pasta
          </Button>
        </div>
      </div>

      <div className={classes.main}>
        <div className={classes.wrap}>
          <div className={classes.header}>
            <Typography className={classes.title}>{selectedFolder ? selectedFolder.name : "Armazenamento"}</Typography>
            <span className={classes.titleCount}>{visibleFiles.length}</span>
          </div>

          <div className={classes.usage}>
            <div className={classes.metric}>
              <span className={classes.metricVal}>{fmtBytes(total)}</span>
              <span className={classes.metricLbl}>Uso total</span>
            </div>
            <div className={classes.metric}>
              <span className={classes.metricVal}>{files.length}</span>
              <span className={classes.metricLbl}>Arquivos</span>
            </div>
            <div className={classes.metric}>
              <span className={classes.metricVal}>{folders.length}</span>
              <span className={classes.metricLbl}>Pastas</span>
            </div>
          </div>

          <div className={classes.tbl}>
            <div className={classes.fhead}>
              <span />
              <span>Nome</span>
              <span>Tamanho</span>
              <span>Enviado em</span>
              <span style={{ textAlign: "right" }}>Ações</span>
            </div>
            {visibleFiles.length === 0 ? (
              <div className={classes.empty}>
                {selectedFolder && selectedFolder.slug === "root"
                  ? "Selecione uma pasta para ver os arquivos."
                  : "Nenhum arquivo nesta pasta."}
              </div>
            ) : (
              visibleFiles.map(file => (
                <div key={file.id} className={classes.frow} style={highlight === file.id ? { background: "#F4FFCC" } : undefined}>
                  {isAudio(file) ? (
                    <IconButton size="small" onClick={() => toggle(file.url)} title={playingUrl === file.url ? "Parar" : "Ouvir"}>
                      {playingUrl === file.url ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                    </IconButton>
                  ) : (
                    <IconButton size="small" onClick={() => openFile(file)} title="Abrir">
                      <FileIcon fontSize="small" />
                    </IconButton>
                  )}
                  <span className={classes.fname}>{file.name}</span>
                  <span className={classes.fmeta}>{fmtBytes(file.sizeBytes)}</span>
                  <span className={classes.fmeta}>{fmtDate(file.createdAt)}</span>
                  <span className={classes.facts}>
                    {file.publicLinkAt && (
                      <IconButton size="small" title="Tem link público — clique para revogar" onClick={() => revokePublic(file)}>
                        <LinkOffIcon fontSize="small" />
                      </IconButton>
                    )}
                    {!isAudio(file) && (
                      <IconButton size="small" title="Abrir" onClick={() => openFile(file)}>
                        <OpenIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" title="Mover" onClick={e => setMoveMenu({ anchor: e.currentTarget, file })}>
                      <DriveFileMoveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" title="Excluir" onClick={() => removeFile(file)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* folder kebab */}
      <Menu anchorEl={folderMenu ? folderMenu.anchor : null} open={!!folderMenu} onClose={() => setFolderMenu(null)}>
        <MenuItem onClick={() => createFolder(folderMenu.folder.id)}>Nova subpasta</MenuItem>
        <MenuItem onClick={() => renameFolder(folderMenu.folder)}>Renomear</MenuItem>
        <MenuItem onClick={() => deleteFolder(folderMenu.folder)} style={{ color: "#C0453B" }}>Excluir</MenuItem>
      </Menu>

      {/* move-file folder picker */}
      <Menu anchorEl={moveMenu ? moveMenu.anchor : null} open={!!moveMenu} onClose={() => setMoveMenu(null)}>
        {folders.filter(f => f.slug !== "root").map(f => (
          <MenuItem key={f.id} disabled={moveMenu && moveMenu.file.folderId === f.id} onClick={() => moveFile(moveMenu.file, f.id)}>
            {f.name}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default Storage;
